import type { ReactElement } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import type { MetricEntry } from '../../api/metricsTypes.js';
import { parseIsoDate } from './bmiUtils.js';

export interface MetricsTableProps {
  entries: MetricEntry[];
  onEdit: (entry: MetricEntry) => void;
  onDelete: (entry: MetricEntry) => void;
}

function formatRowDate(iso: string): string {
  const t = parseIsoDate(iso);
  if (t === null) return iso;
  const d = new Date(t);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function MetricsTable({
  entries,
  onEdit,
  onDelete,
}: MetricsTableProps): ReactElement {
  const cardStyle = {
    background: 'var(--bg-surface)',
    borderColor: 'var(--border)',
    color: 'var(--text-main)',
  } as const;

  if (entries.length === 0) {
    return (
      <section
        aria-label="Measurement log"
        data-testid="metrics-table-card"
        className="rounded-2xl border p-5 shadow-sm"
        style={cardStyle}
      >
        <p
          className="text-sm"
          style={{ color: 'var(--text-muted)' }}
          data-testid="metrics-table-empty"
        >
          No measurements recorded.
        </p>
      </section>
    );
  }

  const rows = [...entries].sort((a, b) => {
    const ta = parseIsoDate(a.measured_at) ?? 0;
    const tb = parseIsoDate(b.measured_at) ?? 0;
    return tb - ta;
  });

  return (
    <section
      aria-label="Measurement log"
      data-testid="metrics-table-card"
      className="rounded-2xl border p-3 shadow-sm sm:p-5"
      style={cardStyle}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr
              className="text-left"
              style={{ color: 'var(--text-muted)' }}
            >
              <th className="px-2 py-2 font-medium">Date</th>
              <th className="px-2 py-2 font-medium">Weight (kg)</th>
              <th className="px-2 py-2 font-medium">Body Fat (%)</th>
              <th className="px-2 py-2 font-medium">Notes</th>
              <th className="px-2 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                data-testid={`metrics-row-${row.id}`}
                className="border-t"
                style={{ borderColor: 'var(--border)' }}
              >
                <td className="px-2 py-2" data-testid={`metrics-row-date-${row.id}`}>
                  {formatRowDate(row.measured_at)}
                </td>
                <td className="px-2 py-2" data-testid={`metrics-row-weight-${row.id}`}>
                  {row.weight_kg.toFixed(1)}
                </td>
                <td className="px-2 py-2" data-testid={`metrics-row-bf-${row.id}`}>
                  {row.body_fat_pct !== null ? row.body_fat_pct.toFixed(1) : '—'}
                </td>
                <td
                  className="max-w-[160px] truncate px-2 py-2"
                  title={row.notes ?? ''}
                  data-testid={`metrics-row-notes-${row.id}`}
                >
                  {row.notes ?? '—'}
                </td>
                <td className="px-2 py-2">
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      aria-label="Edit measurement"
                      onClick={() => onEdit(row)}
                      className="flex min-h-[48px] min-w-[48px] items-center justify-center rounded-md"
                      style={{ color: 'var(--text-main)' }}
                      data-testid={`metrics-row-edit-${row.id}`}
                    >
                      <Pencil size={16} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      aria-label="Delete measurement"
                      onClick={() => onDelete(row)}
                      className="flex min-h-[48px] min-w-[48px] items-center justify-center rounded-md"
                      style={{ color: 'var(--text-main)' }}
                      data-testid={`metrics-row-delete-${row.id}`}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
